// TODO: Assessment remote cleanup, delete this file. See "Cleanup" in docs/RemoteModules.md
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  TAvailabilityCollectionEmbeddedTimeSlot,
  TAvailabilityCollectionPeriod,
} from 'types/availabilityCollectionPeriod.type';
import type { TAvailabilityCollectionTimeslot } from 'types/availabilityCollectionTimeslot.type';
import type { IDefaultReducerState } from 'types/state.interface';
import { errorNotification } from 'utils/notifications';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { validateAvailabilityPeriod } from 'utils/validations/availabilityCollectionSettings.validation';
import api from '../services/api.service';
import { fetchTimeZones } from './timeZone.slice';

dayjs.extend(utc);
dayjs.extend(timezone);

export type TAvailabilityCollectionPeriodState = IDefaultReducerState & {
  data: TAvailabilityCollectionPeriod[];
  selectedPeriod?: TAvailabilityCollectionPeriod | null;
};

export const initialState: TAvailabilityCollectionPeriodState = {
  loading: false,
  hasErrors: false,
  selectedPeriod: null,
  data: [],
};

const createEmptyPeriod = (): TAvailabilityCollectionPeriod => ({
  id: 'new',
  name: '',
  periodStart: dayjs().format('YYYY-MM-DD'),
  periodEnd: dayjs().add(1, 'day').format('YYYY-MM-DD'),
  periodDeadline: dayjs().format('YYYY-MM-DD'),
  description: undefined,
  timeSlots: [],
});

// Slice
const slice = createSlice({
  name: 'availabilityCollectionPeriod',
  initialState,
  reducers: {
    fetchAvailabilityCollectionPeriodsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchAvailabilityCollectionPeriodsSuccess: (
      state,
      { payload }: PayloadAction<TAvailabilityCollectionPeriod[]>
    ) => {
      state.data = payload;
      finishedLoadingSuccess(state);
    },
    fetchAvailabilityCollectionPeriodsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createAvailabilityCollectionPeriodRequest: (state) => {
      isLoadingRequest(state);
    },
    createAvailabilityCollectionPeriodSuccess: (
      state,
      { payload }: PayloadAction<TAvailabilityCollectionPeriod>
    ) => {
      state.data.push(payload);
      state.selectedPeriod = null;
      finishedLoadingSuccess(state);
    },
    createAvailabilityCollectionPeriodFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateAvailabilityCollectionPeriodRequest: (state) => {
      isLoadingRequest(state);
    },
    updateAvailabilityCollectionPeriodSuccess: (
      state,
      { payload }: PayloadAction<TAvailabilityCollectionPeriod>
    ) => {
      const findIndex = state.data.findIndex((item) => item.id === payload.id);
      if (findIndex !== -1) {
        state.data[findIndex] = payload;
        state.selectedPeriod = null;
      }
      finishedLoadingSuccess(state);
    },
    updateAvailabilityCollectionPeriodFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteAvailabilityCollectionPeriodRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteAvailabilityCollectionPeriodSuccess: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      state.data = state.data.filter((item) => item.id !== payload);
      state.selectedPeriod = null;
      finishedLoadingSuccess(state);
    },
    deleteAvailabilityCollectionPeriodFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createUnsavePeriod: (state) => {
      state.selectedPeriod = createEmptyPeriod();
    },
    deleteUnsavePeriod: (state) => {
      state.selectedPeriod = null;
    },
    resetPeriodChanges: (state) => {
      if (!state.selectedPeriod) return;

      if (state.selectedPeriod.id === 'new') {
        state.selectedPeriod = createEmptyPeriod();
        return;
      }

      const originalPeriod = state.data.find(
        (item) => item.id === state.selectedPeriod.id
      );

      if (originalPeriod) {
        state.selectedPeriod = { ...originalPeriod };
        return;
      }
      state.selectedPeriod = null;
    },
    setSelectedPeriod: (state, { payload: period }) => {
      state.selectedPeriod = period;
    },
    updatedSelectedPeriod: (
      state,
      { payload }: PayloadAction<Partial<TAvailabilityCollectionPeriod>>
    ) => {
      if (!state.selectedPeriod) return;

      state.selectedPeriod = {
        ...state.selectedPeriod,
        ...payload,
      };
    },
  },
});

export default slice.reducer;

// Selectors
export const availabilityCollectionPeriodsSelector = (
  state: TRootState
): TAvailabilityCollectionPeriod[] => state.availabilityCollectionPeriod.data;

export const availabilityCollectionPeriodsLoadingSelector = (
  state: TRootState
): boolean => state.availabilityCollectionPeriod.loading;

export const availabilityCollectionPeriodsSelectedPeriodSelector = (
  state: TRootState
): TAvailabilityCollectionPeriod | null =>
  state.availabilityCollectionPeriod.selectedPeriod;

// Actions
export const {
  fetchAvailabilityCollectionPeriodsRequest,
  fetchAvailabilityCollectionPeriodsSuccess,
  fetchAvailabilityCollectionPeriodsFailure,
  createAvailabilityCollectionPeriodRequest,
  createAvailabilityCollectionPeriodSuccess,
  createAvailabilityCollectionPeriodFailure,
  updateAvailabilityCollectionPeriodRequest,
  updateAvailabilityCollectionPeriodSuccess,
  updateAvailabilityCollectionPeriodFailure,
  deleteAvailabilityCollectionPeriodRequest,
  deleteAvailabilityCollectionPeriodSuccess,
  deleteAvailabilityCollectionPeriodFailure,
  createUnsavePeriod,
  deleteUnsavePeriod,
  updatedSelectedPeriod,
  setSelectedPeriod,
  resetPeriodChanges,
} = slice.actions;

const normalizePeriod = (
  period: TAvailabilityCollectionPeriod,
  timezoneIdentifier: string
) => {
  const normalizeDate = (dateStr: string) =>
    dayjs(dateStr).tz(timezoneIdentifier).format('YYYY-MM-DD');
  return {
    ...period,
    periodStart: normalizeDate(period.periodStart),
    periodEnd: normalizeDate(period.periodEnd),
    periodDeadline: period.periodDeadline
      ? normalizeDate(period.periodDeadline)
      : null,
    timeSlots: period.timeSlots?.map(
      availableCollectionPeriodToEmbeddedTimeSlot
    ),
  };
};

export const availableCollectionPeriodToEmbeddedTimeSlot = (
  period: TAvailabilityCollectionTimeslot
): TAvailabilityCollectionEmbeddedTimeSlot => {
  return {
    _id: period._id,
    startTime: period.startTime,
    endTime: period.endTime,
    name: period.name,
    weekdays: period.weekdays,
  };
};

export const fetchAvailabilityCollectionPeriods = createAppAsyncThunk(
  'availabilityCollectionSettings/fetchAvailabilityCollectionPeriods',
  async (_, { dispatch, getState }) => {
    try {
      // Ensure we have the timezones loaded
      await dispatch(fetchTimeZones());

      dispatch(fetchAvailabilityCollectionPeriodsRequest());
      const result = await api.get({
        endpoint: `/exam/availability/period?includeTimeSlots=true`,
      });

      const defaultTimezone =
        getState().timeZones.allTimeZones.find((zone) => zone.isDefault)
          ?.identifiers?.[0] ?? 'UTC';

      const normalized = result.map((period: TAvailabilityCollectionPeriod) =>
        normalizePeriod(period, defaultTimezone)
      );

      dispatch(fetchAvailabilityCollectionPeriodsSuccess(normalized));
    } catch (e) {
      dispatch(fetchAvailabilityCollectionPeriodsFailure());
      console.error(e);
    }
  }
);

export const createAvailabilityCollectionPeriod = createAppAsyncThunk(
  'availabilityCollectionSettings/createAvailabilityCollectionPeriod',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(createAvailabilityCollectionPeriodRequest());
      const state = getState();

      const { id, ...selectedPeriod } =
        state.availabilityCollectionPeriod.selectedPeriod;
      const validated = validateAvailabilityPeriod(selectedPeriod);

      if (!validated.success) {
        errorNotification(
          'Configuration could not be validated',
          validated.error.issues.map((error) => error.message)
        );
        throw new Error('Configuration could not be validated');
      }
      const result = await api.post({
        endpoint: `/exam/availability/period`,
        data: selectedPeriod,
      });

      const defaultTimezone =
        getState().timeZones.allTimeZones.find((zone) => zone.isDefault)
          ?.identifiers?.[0] ?? 'UTC';

      const newPeriod = normalizePeriod(result, defaultTimezone);

      dispatch(createAvailabilityCollectionPeriodSuccess(newPeriod));
    } catch (e) {
      dispatch(createAvailabilityCollectionPeriodFailure());
      console.error(e);
    }
  }
);

export const updateAvailabilityCollectionPeriod = createAppAsyncThunk(
  'availabilityCollectionSettings/updateAvailabilityCollectionPeriod',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(updateAvailabilityCollectionPeriodRequest());
      const state = getState();
      const { id, ...selectedPeriod } =
        state.availabilityCollectionPeriod.selectedPeriod;

      const validated = validateAvailabilityPeriod(selectedPeriod);

      if (!validated.success) {
        errorNotification(
          'Configuration could not be validated',
          validated.error.issues.map((error) => error.message)
        );
        throw new Error('Configuration could not be validated');
      }

      const result = await api.put({
        endpoint: `/exam/availability/period/${id}`,
        data: selectedPeriod,
      });

      const defaultTimezone =
        getState().timeZones.allTimeZones.find((zone) => zone.isDefault)
          ?.identifiers?.[0] ?? 'UTC';

      const updatedPeriod = normalizePeriod(result, defaultTimezone);

      dispatch(updateAvailabilityCollectionPeriodSuccess(updatedPeriod));
    } catch (e) {
      dispatch(updateAvailabilityCollectionPeriodFailure());
      console.error(e);
    }
  }
);

export const deleteAvailabilityCollectionPeriod = createAppAsyncThunk(
  'availabilityCollectionSettings/deleteAvailabilityCollectionPeriod',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(deleteAvailabilityCollectionPeriodRequest());
      const state = getState();
      const { id } = state.availabilityCollectionPeriod.selectedPeriod;
      await api.delete({
        endpoint: `/exam/availability/period/${id}`,
      });
      dispatch(deleteAvailabilityCollectionPeriodSuccess(id));
    } catch (e) {
      dispatch(deleteAvailabilityCollectionPeriodFailure());
      console.error(e);
    }
  }
);
